/**
 * Hộp thoại cấp quyền theo email — dùng ở hai nơi:
 *   - `permissions.html` → *Thêm người dùng* (data-od-id="modal-add-user")
 *   - `domain-detail.html` → *Chia sẻ quyền* (data-od-id="modal-share")
 *
 * Trang `/domains/:id` thuộc mốc M4 (Agent A). Component này được XUẤT ra để
 * trang đó import, M5 không sửa trang của mốc khác.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { Modal } from '../../components/Modal';
import { SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import { EmailGrantFlow, type EmailGrantFlowState } from './EmailGrantFlow';
import { permApi, permKeys } from './api';

export function ShareGrantDialog({
  open,
  domainId,
  domainName,
  variant = 'permissions',
  onClose,
}: {
  open: boolean;
  domainId: string;
  domainName: string;
  /** `permissions` = modal-add-user · `domain-detail` = modal-share. */
  variant?: 'permissions' | 'domain-detail';
  onClose: () => void;
}) {
  const t = useT();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();
  const [flow, setFlow] = useState<EmailGrantFlowState | null>(null);

  const rolesQuery = useQuery({
    queryKey: permKeys.domainRoles,
    queryFn: permApi.domainRoles,
    staleTime: 10 * 60 * 1000,
    enabled: open,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: permKeys.all });

  const send = useMutation({
    mutationFn: async () => {
      const payload = flow?.submit();
      if (!payload) throw new ApiError(422, 'VALIDATION_ERROR', t('perm.lookupInvalid'));
      if (payload.kind === 'grant') {
        const grant = await permApi.createGrant(domainId, {
          userId: payload.userId,
          domainRoleId: payload.domainRoleId,
          notify: payload.notify,
        });
        return { kind: 'grant' as const, label: grant.user.fullName || grant.user.email };
      }
      const result = await permApi.invite(domainId, {
        email: payload.email,
        domainRoleId: payload.domainRoleId,
        displayName: payload.displayName,
        message: payload.message,
        expiresInDays: payload.expiresInDays,
      });
      return result.mode === 'granted'
        ? { kind: 'grant' as const, label: result.grant.user.fullName || result.grant.user.email }
        : { kind: 'invite' as const, label: result.pending.email };
    },
    onSuccess: async (result) => {
      await invalidate();
      toast(
        result.kind === 'grant'
          ? t('perm.granted', { domain: domainName, name: result.label })
          : t('perm.invited', { email: result.label }),
      );
      flow?.reset();
      onClose();
    },
    onError: (err) => toastError(err instanceof ApiError ? err.message : t('state.error.title')),
  });

  const isShare = variant === 'domain-detail';

  return (
    <>
      {/* Giữ đúng `data-od-id` của prototype cho selector E2E (docs/08 §8.9). */}
      <div data-od-id={isShare ? 'modal-share' : 'modal-add-user'} hidden />
      <Modal
        open={open}
        title={isShare ? t('perm.shareTitle', { name: domainName }) : t('perm.addUserTitle')}
        subtitle={isShare ? t('perm.shareSub') : t('perm.addUserSub', { name: domainName })}
        onClose={onClose}
        onSubmit={() => send.mutate()}
        footer={
          <>
            <button className="btn" type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={!flow?.canSubmit || send.isPending}
              data-od-id={isShare ? 'share-submit' : 'add-user-submit'}
            >
              {send.isPending ? t('common.processing') : (flow?.submitLabel ?? t('perm.grant'))}
            </button>
          </>
        }
      >
        {rolesQuery.isLoading ? (
          <SkeletonCard rows={3} />
        ) : (
          <EmailGrantFlow
            domainId={domainId}
            roles={rolesQuery.data ?? []}
            idPrefix={isShare ? 'share' : 'au'}
            onStateChange={setFlow}
          />
        )}
      </Modal>
    </>
  );
}
